import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Grid,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  makeStyles,
  Divider
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Visibility as PreviewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import useEmailTemplates from "../hooks/useEmailTemplates";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 12
  },
  table: {
    minWidth: 650
  },
  statusChip: {
    marginRight: theme.spacing(1)
  },
  dialogContent: {
    paddingTop: theme.spacing(2)
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end"
  },
  editorContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1)
  },
  htmlEditor: {
    fontFamily: "monospace",
    fontSize: "13px",
    minHeight: "300px",
    width: "100%",
    border: "none",
    outline: "none",
    resize: "vertical"
  },
  previewContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    minHeight: "300px",
    backgroundColor: "#ffffff", // Fondo blanco fijo para preview de email
    color: "#000000", // Texto negro para mejor contraste
    overflow: "auto",
    // Simular cliente de email con fondo claro
    boxShadow: theme.mode === 'dark' 
      ? 'inset 0 0 10px rgba(0,0,0,0.1)' 
      : 'inset 0 0 10px rgba(0,0,0,0.05)'
  },
  variablesList: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1.5),
    borderRadius: 4,
    marginTop: theme.spacing(1)
  },
  variableChip: {
    margin: theme.spacing(0.5),
    cursor: "pointer"
  },
  tabPanel: {
    padding: theme.spacing(2, 0)
  }
}));

// Variables disponibles por propósito
const PURPOSE_VARIABLES = {
  welcome: ["userName", "companyName", "loginLink"],
  password_reset: ["userName", "resetToken", "resetLink"],
  vps_alert: [
    "serverName",
    "serverIP",
    "alertType",
    "diskUsage",
    "memoryUsage",
    "cpuUsage",
    "binaryLogsSize"
  ],
  vps_alert_disk: [
    "serverName",
    "serverIP",
    "diskUsage",
    "diskTotal",
    "diskUsed",
    "diskAvailable"
  ],
  vps_alert_binary: [
    "serverName",
    "serverIP",
    "binaryLogsSize",
    "binaryLogsCount"
  ],
  vps_alert_cpu: ["serverName", "serverIP", "cpuUsage"],
  vps_alert_memory: [
    "serverName",
    "serverIP",
    "memoryUsage",
    "memoryTotal",
    "memoryUsed"
  ],
  report: ["reportName", "reportDate", "reportData"],
  unsubscribe: ["userName", "unsubscribeLink"],
  custom: []
};

// Propósitos disponibles
const PURPOSES = [
  { value: "welcome", label: "Bienvenida" },
  { value: "password_reset", label: "Cambio de Contraseña" },
  { value: "vps_alert", label: "Alerta VPS (General)" },
  { value: "vps_alert_disk", label: "Alerta VPS - Disco" },
  { value: "vps_alert_binary", label: "Alerta VPS - Binary Logs" },
  { value: "vps_alert_cpu", label: "Alerta VPS - CPU" },
  { value: "vps_alert_memory", label: "Alerta VPS - Memoria" },
  { value: "report", label: "Reporte" },
  { value: "unsubscribe", label: "Darse de Baja" },
  { value: "custom", label: "Personalizado" }
];

const EmailTemplateManager = () => {
  const classes = useStyles();
  const {
    loading,
    templates,
    selectedTemplate,
    filterPurpose,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    setSelectedTemplate,
    setFilterPurpose
  } = useEmailTemplates();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dialogTab, setDialogTab] = useState(0); // 0: Editor, 1: Preview
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    purpose: "custom",
    subject: "",
    htmlContent: "",
    textContent: "",
    variables: []
  });

  // Variables disponibles según propósito
  const availableVariables = useMemo(() => {
    return PURPOSE_VARIABLES[formData.purpose] || [];
  }, [formData.purpose]);

  // Preview con datos de ejemplo
  const previewContent = useMemo(() => {
    if (!formData.htmlContent) return "";
    
    let preview = formData.htmlContent;
    const exampleData = {
      userName: "Juan Pérez",
      companyName: "Mi Empresa",
      loginLink: "https://app.ejemplo.com/login",
      resetToken: "ABC123XYZ",
      resetLink: "https://app.ejemplo.com/reset?token=ABC123XYZ",
      serverName: "Servidor Principal",
      serverIP: "192.168.1.100",
      alertType: "disk",
      diskUsage: "85%",
      diskTotal: "500GB",
      diskUsed: "425GB",
      diskAvailable: "75GB",
      memoryUsage: "70%",
      memoryTotal: "16GB",
      memoryUsed: "11.2GB",
      cpuUsage: "45%",
      binaryLogsSize: "25GB",
      binaryLogsCount: "150",
      reportName: "Reporte Semanal",
      reportDate: new Date().toLocaleDateString(),
      reportData: "Datos del reporte...",
      unsubscribeLink: "https://app.ejemplo.com/unsubscribe"
    };

    // Reemplazar variables con datos de ejemplo
    availableVariables.forEach((varName) => {
      const regex = new RegExp(`\\{${varName}\\}`, "g");
      const value = exampleData[varName] || `[${varName}]`;
      preview = preview.replace(regex, value);
    });

    return preview;
  }, [formData.htmlContent, availableVariables]);

  /**
   * Abrir diálogo para crear nueva plantilla
   */
  const handleOpenCreate = () => {
    setIsEditing(false);
    setDialogTab(0);
    setFormData({
      name: "",
      description: "",
      purpose: "custom",
      subject: "",
      htmlContent: "",
      textContent: "",
      variables: []
    });
    setDialogOpen(true);
  };

  /**
   * Abrir diálogo para editar plantilla
   */
  const handleOpenEdit = (template) => {
    setIsEditing(true);
    setDialogTab(0);
    setSelectedTemplate(template);
    setFormData({
      name: template.name || "",
      description: template.description || "",
      purpose: template.purpose || "custom",
      subject: template.subject || "",
      htmlContent: template.htmlContent || "",
      textContent: template.textContent || "",
      variables: template.variables || []
    });
    setDialogOpen(true);
  };

  /**
   * Cerrar diálogo
   */
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTemplate(null);
  };

  /**
   * Insertar variable en el editor
   */
  const handleInsertVariable = (varName) => {
    const variable = `{${varName}}`;
    const currentContent = formData.htmlContent;
    const cursorPos = document.activeElement?.selectionStart || currentContent.length;
    const newContent =
      currentContent.slice(0, cursorPos) +
      variable +
      currentContent.slice(cursorPos);
    setFormData({ ...formData, htmlContent: newContent });
    toast.info(`Variable ${variable} insertada`);
  };

  /**
   * Guardar plantilla
   */
  const handleSave = async () => {
    try {
      // Actualizar variables según las que estén en el contenido
      const usedVariables = [];
      availableVariables.forEach((varName) => {
        if (formData.htmlContent.includes(`{${varName}}`)) {
          usedVariables.push(`{${varName}}`);
        }
      });

      const templateData = {
        ...formData,
        variables: usedVariables
      };

      if (isEditing && selectedTemplate) {
        await updateTemplate(selectedTemplate.id, templateData);
      } else {
        await createTemplate(templateData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Error guardando plantilla:", error);
    }
  };

  /**
   * Eliminar plantilla
   */
  const handleDelete = async (id) => {
    if (
      window.confirm(
        "¿Estás seguro de que deseas eliminar esta plantilla?"
      )
    ) {
      await deleteTemplate(id);
    }
  };

  /**
   * Duplicar plantilla
   */
  const handleDuplicate = async (template) => {
    await duplicateTemplate(template);
  };

  // Filtrar plantillas por propósito
  const filteredTemplates = useMemo(() => {
    if (!filterPurpose) return templates;
    return templates.filter((t) => t.purpose === filterPurpose);
  }, [templates, filterPurpose]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h6" component="h2">
          Plantillas de Email
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Filtrar por propósito</InputLabel>
            <Select
              value={filterPurpose}
              onChange={(e) => setFilterPurpose(e.target.value)}
              label="Filtrar por propósito"
            >
              <MenuItem value="">Todos</MenuItem>
              {PURPOSES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Nueva Plantilla
          </Button>
        </Box>
      </div>

      <Card className={classes.card}>
        <CardContent>
          {loading && templates.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : filteredTemplates.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              p={3}
            >
              No hay plantillas. Crea una nueva para comenzar.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Propósito</TableCell>
                    <TableCell>Asunto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight={500}>
                            {template.name}
                          </Typography>
                          {template.isSystemTemplate === 1 ||
                          template.isSystemTemplate === true ? (
                            <Chip
                              label="Sistema"
                              size="small"
                              color="secondary"
                              className={classes.statusChip}
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            PURPOSES.find((p) => p.value === template.purpose)
                              ?.label || template.purpose
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap style={{ maxWidth: 300 }}>
                          {template.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            template.isSystemTemplate === 1 ||
                            template.isSystemTemplate === true
                              ? "Sistema"
                              : "Usuario"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            template.isActive === 1 ||
                            template.isActive === true ? (
                              <CheckCircleIcon />
                            ) : (
                              <CancelIcon />
                            )
                          }
                          label={
                            template.isActive === 1 ||
                            template.isActive === true
                              ? "Activo"
                              : "Inactivo"
                          }
                          size="small"
                          color={
                            template.isActive === 1 ||
                            template.isActive === true
                              ? "primary"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Vista previa">
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleOpenEdit(template);
                              setDialogTab(1);
                            }}
                          >
                            <PreviewIcon />
                          </IconButton>
                        </Tooltip>
                        {!(template.isSystemTemplate === 1 || template.isSystemTemplate === true) && (
                          <>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(template)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicar">
                              <IconButton
                                size="small"
                                onClick={() => handleDuplicate(template)}
                              >
                                <DuplicateIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(template.id)}
                            disabled={
                              template.isSystemTemplate === 1 ||
                              template.isSystemTemplate === true
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de creación/edición */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? "Editar Plantilla" : "Nueva Plantilla"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Tabs
            value={dialogTab}
            onChange={(e, newValue) => setDialogTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Editor" />
            <Tab label="Vista Previa" />
          </Tabs>

          {dialogTab === 0 && (
            <div className={classes.tabPanel}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className={classes.formField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth className={classes.formField}>
                    <InputLabel>Propósito</InputLabel>
                    <Select
                      value={formData.purpose}
                      onChange={(e) =>
                        setFormData({ ...formData, purpose: e.target.value })
                      }
                    >
                      {PURPOSES.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    multiline
                    rows={2}
                    className={classes.formField}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Asunto"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                    required
                    className={classes.formField}
                    placeholder="Puede contener variables: {userName}, {resetToken}, etc."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Variables Disponibles (haz clic para insertar):
                  </Typography>
                  <Box className={classes.variablesList}>
                    {availableVariables.length > 0 ? (
                      availableVariables.map((varName) => (
                        <Chip
                          key={varName}
                          label={`{${varName}}`}
                          size="small"
                          className={classes.variableChip}
                          onClick={() => handleInsertVariable(varName)}
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No hay variables predefinidas para este propósito
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Contenido HTML
                  </Typography>
                  <div className={classes.editorContainer}>
                    <TextField
                      fullWidth
                      multiline
                      rows={15}
                      value={formData.htmlContent}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          htmlContent: e.target.value
                        })
                      }
                      required
                      className={classes.htmlEditor}
                      placeholder="Ingresa el HTML de la plantilla. Usa {variableName} para variables."
                      InputProps={{
                        className: classes.htmlEditor
                      }}
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contenido Texto Plano (Opcional)"
                    value={formData.textContent}
                    onChange={(e) =>
                      setFormData({ ...formData, textContent: e.target.value })
                    }
                    multiline
                    rows={4}
                    className={classes.formField}
                    placeholder="Versión texto plano del email (opcional)"
                  />
                </Grid>
              </Grid>
            </div>
          )}

          {dialogTab === 1 && (
            <div className={classes.tabPanel}>
              <Typography variant="subtitle2" gutterBottom>
                Vista Previa del Email
              </Typography>
              <Divider style={{ marginBottom: 16 }} />
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Asunto:</strong> {formData.subject || "(sin asunto)"}
              </Typography>
              <Box
                className={classes.previewContainer}
                style={{
                  backgroundColor: "#ffffff",
                  color: "#000000"
                }}
              >
                {/* 🔒 M-07 (doc 0431): antes el HTML de la plantilla se insertaba en el panel
                    tal cual (dangerouslySetInnerHTML): un <script> o un onerror se ejecutaba con
                    la sesión de quien abría la vista previa. En un iframe con sandbox vacío no
                    ejecuta nada, y los estilos del email ya no se mezclan con los del panel. */}
                <iframe
                  title="Vista previa del email"
                  sandbox=""
                  srcDoc={previewContent}
                  style={{
                    width: "100%",
                    minHeight: "300px",
                    border: "none",
                    backgroundColor: "#ffffff"
                  }}
                />
              </Box>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={() => setDialogTab(1)}
            disabled={dialogTab === 1}
            color="primary"
          >
            Vista Previa
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={
              loading ||
              !formData.name ||
              !formData.purpose ||
              !formData.subject ||
              !formData.htmlContent
            }
          >
            {loading ? <CircularProgress size={20} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmailTemplateManager;
